import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParamarshDetails } from "../../../utils";
import { useSelector } from "react-redux";
import Accordion from "react-bootstrap/Accordion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ReactGA from "react-ga4";
import {
  DIGITS_ONLY_PATTERN,
  numericInputProps,
  sanitizeDigitsOnly,
} from "../../../utils/inputConstraints";
import Header from "../../layout/Header";
import { Loader } from "../../loader";

const BANK_PAYMENT_DRAFT_KEY = "strongram_bank_payment_draft_v1";

export const BankDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    clearErrors,
  } = useForm();
  const user = useSelector((state) => state.user);
  const [errorMessage, setErrorMessage] = useState("");
  const claimValue = useSelector((state) => state.claimValue);
  const [fetchData, rewardsData, , , cashRewardsData, loader] = useParamarshDetails();
  const amount = useSelector((state) => state.amount);
  const reclaimDataObj = useSelector((state) => state.reclaimDataObj);
  const [searchParams] = useSearchParams();
  const isreclaim = searchParams.get("isreclaim");

  const [formData, setFormData] = useState({
    accountName: "test",
    accountNumber: "",
    cashFreeTransactionId: 0,
    cashRewards: "",
    claimIDs: [],
    dsrProgramId: 0,
    ecouponFarmerMapId: 0,
    ifscCode: "",
    paymentMode: "banktransfer",
    phoneNumber: sessionStorage.getItem("mbn") || "",
    seadProgramId: 0,
    destinationURL: "/cashFreeReward/cashFreePaymentModeRequest_V2",
  });

  useEffect(() => {
    if (!claimValue) {
      setFormData((prevData) => ({
        ...prevData,
        cashFreeTransactionId: reclaimDataObj.cashFreeTransactionId,
        cashRewards: reclaimDataObj.amount,
        claimIDs: [],
      }));
    } else {
      fetchData();
    }
  }, [claimValue, reclaimDataObj, fetchData]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(BANK_PAYMENT_DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.accountName != null && d.accountName !== "") {
        setValue("accountName", d.accountName);
      }
      if (d.accountNumber) {
        const acct = sanitizeDigitsOnly(d.accountNumber, 20);
        setValue("accountNumber", acct);
        setValue("confirmaccountNumber", acct);
      }
      if (d.ifscCode) setValue("ifscCode", d.ifscCode);
      setFormData((prev) => ({
        ...prev,
        accountName:
          d.accountName != null && d.accountName !== ""
            ? d.accountName
            : prev.accountName,
        accountNumber: d.accountNumber || prev.accountNumber,
        ifscCode: d.ifscCode || prev.ifscCode,
        phoneNumber: d.phoneNumber || prev.phoneNumber,
        mobileNumber: d.mobileNumber || prev.mobileNumber,
      }));
    } catch {
      // ignore malformed draft
    }
  }, [claimValue, setValue]);

  const handleChange = (fieldName, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: value,
      cashRewards: claimValue ? cashRewardsData : amount,
      claimIDs: claimValue ? rewardsData : [],
    }));
    clearErrors(fieldName);
  };

  const handleAccountNumberChange = (fieldName, rawValue) => {
    const digits = sanitizeDigitsOnly(rawValue, 20);
    setValue(fieldName, digits, { shouldValidate: true });
    handleChange(fieldName, digits);
  };

  const onSubmit = () => {
    ReactGA.event({
      category: "User Bank Details",
      action: "CLAIM_BANK_BTN",
    });

    const phone =
      user?.mobileNumber || sessionStorage.getItem("mbn") || "";

    const accountNameFromForm = watch("accountName");
    const ifscFromForm = watch("ifscCode");

    const enriched = {
      ...formData,
      phoneNumber: phone,
      accountName: accountNameFromForm || formData.accountName,
      ifscCode: (ifscFromForm || formData.ifscCode || "").trim().toUpperCase(),
    };

    if (claimValue) {
      enriched.cashRewards =
        cashRewardsData !== undefined && cashRewardsData !== ""
          ? cashRewardsData
          : enriched.cashRewards;
      enriched.claimIDs =
        Array.isArray(rewardsData) && rewardsData.length > 0
          ? rewardsData
          : enriched.claimIDs || [];
    } else {
      enriched.cashRewards = reclaimDataObj?.amount ?? enriched.cashRewards;
      enriched.claimIDs = [];
      enriched.cashFreeTransactionId =
        reclaimDataObj?.cashFreeTransactionId ??
        enriched.cashFreeTransactionId;
    }

    const missingClaimIds =
      !!claimValue &&
      (!Array.isArray(enriched.claimIDs) || enriched.claimIDs.length === 0);

    if (
      !enriched.cashRewards ||
      !enriched.accountNumber ||
      missingClaimIds
    ) {
      setErrorMessage(t("sessionExpired"));
      setTimeout(() => {
        navigate(`/${lang}/home`);
      }, 2000);
      return;
    }

    sessionStorage.setItem(
      BANK_PAYMENT_DRAFT_KEY,
      JSON.stringify(enriched)
    );
    navigate(`/${lang}/confirmBankDetails`);
  };

  const hide = () => {
    setErrorMessage("");
  };

  const accountNumber = watch("accountNumber");
  const headerAmount =
    !claimValue && reclaimDataObj?.amount != null
      ? reclaimDataObj.amount
      : amount;

  return (
    <>
      {loader && <Loader />}
      <Header />
      <div className="main">
        <div className="container p-0">
          <div className="card border-0">
            <Accordion.Header id="transfer">
              <div>
                <i
                  className="fa fa-chevron-left text-white mr-2"
                  onClick={() => navigate(`/${lang}/home`)}
                />
                {t("transfer")}
              </div>
              <span className="float-end">
                <span>₹ {amount ? amount : cashRewardsData}</span>
              </span>
            </Accordion.Header>
            <div className="card-body px-2 pt-0 pb-2">
              <div className="px-4 py-2 mt-3">
                <i
                  className="fa fa-arrow-left mr-2"
                  onClick={() => navigate(`/${lang}/paymentmode?isreclaim=${isreclaim}`)}
                />
                <span className="infoText mb-0">{t("bankDetails")}</span>
                <hr className="mt-2" />

                <div className="mt-5">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div>
                      <input
                        type="text"
                        placeholder={t("accountHolderName")}
                        name="accountName"
                        id="accountName"
                        className={`form-control bankInput mb-2 ${errors.accountName ? "is-invalid" : ""
                          }`}
                        {...register("accountName", {
                          required: t("accountHolderNameErrorMsg"),
                        })}
                        onChange={(e) =>
                          handleChange("accountName", e.target.value)
                        }
                      />
                      {errors.accountName && (
                        <div className="invalid-feedback">
                          {errors.accountName.message}
                        </div>
                      )}
                    </div>

                    <div>
                      <input
                        {...numericInputProps}
                        placeholder={t("accountNumber")}
                        name="accountNumber"
                        id="accountNumber"
                        maxLength={20}
                        className={`form-control bankInput mb-2 ${errors.accountNumber ? "is-invalid" : ""
                          }`}
                        {...register("accountNumber", {
                          required: t("accountNumberErrorMsg"),
                          pattern: {
                            value: DIGITS_ONLY_PATTERN,
                            message: t("accountNumberErrorMsg"),
                          },
                          onChange: (e) =>
                            handleAccountNumberChange(
                              "accountNumber",
                              e.target.value
                            ),
                        })}
                      />
                      {errors.accountNumber && (
                        <div className="invalid-feedback">
                          {errors.accountNumber.message}
                        </div>
                      )}
                    </div>

                    <div>
                      <input
                        {...numericInputProps}
                        placeholder={t("confirmaccountNumber")}
                        name="confirmaccountNumber"
                        id="confirmaccountNumber"
                        maxLength={20}
                        className={`form-control bankInput mb-2 ${errors.confirmaccountNumber ? "is-invalid" : ""
                          }`}
                        {...register("confirmaccountNumber", {
                          required: t("confirmaccountNumberErrorMsg"),
                          pattern: {
                            value: DIGITS_ONLY_PATTERN,
                            message: t("confirmaccountNumberErrorMsg"),
                          },
                          validate: (value) =>
                            value === accountNumber ||
                            t("confirmAccountNumberMismatch"),
                          onChange: (e) =>
                            handleAccountNumberChange(
                              "confirmaccountNumber",
                              e.target.value
                            ),
                        })}
                      />
                      {errors.confirmaccountNumber && (
                        <div className="invalid-feedback">
                          {typeof errors.confirmaccountNumber.message ===
                            "string" ? (
                            errors.confirmaccountNumber.message
                          ) : (
                            <span>Invalid value</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <input
                        type="text"
                        placeholder={t("IFSCCode")}
                        name="ifscCode"
                        id="ifscCode"
                        className={`form-control bankInput mb-2 ${errors.ifscCode ? "is-invalid" : ""
                          }`}
                        {...register("ifscCode", {
                          required: t("IFSCCodeErrorMsg"),
                        })}
                        onChange={(e) =>
                          handleChange("ifscCode", e.target.value)
                        }
                      />
                      {errors.ifscCode && (
                        <div className="invalid-feedback">
                          {errors.ifscCode.message}
                        </div>
                      )}
                    </div>

                    <div className="selectPaymentFooter">
                      <button type="submit" className="btn btn-secondary w-100 py-2 btnshadow center-textbtn mt-3 text-white text-uppercase" id="submitBtn">{t("confirm")}</button>
                    </div>

                    {/* <button
                      type="submit"
                      className="btn btn-secondary w-100 py-3
                      center-textbtn mt-3 text-white text-uppercase
                      registerSubmitBtn"
                      id="submitBtn"
                    >
                      {t("confirm")}
                    </button> */}
                  </form>
                </div>

                {errorMessage && (
                  <div
                    className="alert alert-warning alert-dismissible fade show mt-2"
                    role="alert"
                  >
                    {errorMessage}
                    <button type="button" className="close" onClick={hide}>
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
